/**
 * useAllOperations
 *
 * Client-side prefetch + search infrastructure for the operation picker.
 * Mirrors the upstream `libs/designer-v2/src/lib/core/queries/browse.ts`
 * pattern (three sources: Azure paginated, Custom paginated, BuiltIn single
 * fetch) and layers an IndexedDB cache on top so subsequent sessions hydrate
 * the operations corpus before any network call lands.
 *
 *   ┌─ usePrefetchOperations ─┐
 *   │  ├ Azure (paged)        │  Cached + merged
 *   │  ├ Custom (paged)       │ ──────────────►  useAllOperations
 *   │  └ BuiltIn (single)     │
 *   └─────────────────────────┘
 *
 * See {@link usePrefetchOperations} for the recommended root-component wiring
 * and {@link useAllOperations} (plus {@link useAllTriggers} /
 * {@link useAllActions}) for the consumer hooks.
 */

import { useEffect, useMemo, useRef } from 'react';
import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/auth/useAuth';
import { useAppStore } from '@/stores/appStore';
import {
  fetchAllAzureConnectors,
  fetchAzureOperationsByPage,
  fetchBuiltInOperations,
  type Connector,
  type DiscoveryOperation,
  type PagedOperationsResult,
} from '@/services/operationsService';
import { getClientBuiltInOperations } from '@/data/clientOperations';
import {
  readOperationsCache,
  trimOperationForCache,
  writeOperationsCache,
} from '@/services/operationsCache';
import { isLocalMode } from '@/services/localRuntimeClient';
import { isArmMockMode } from '@/services/armConfig';
import {
  buildConnectorIdentityIndex,
  isMalformedManagedApiId,
  isMalformedOpId,
  repairAzureOpIdentity,
} from '@/services/operationsRepair';
import { countMalformedAzureIds } from '@/services/operationsDebug';

/** Stable query-cache settings shared across the three source queries. */
const queryOpts = {
  staleTime: 1000 * 60 * 60 * 24, // 24 hours — matches the IDB TTL.
  gcTime: 1000 * 60 * 60 * 24,
  refetchOnMount: false,
  refetchOnWindowFocus: false,
  refetchOnReconnect: false,
};

/** Exposed for unit tests — internal helpers, not part of the public hook API. */
export const __testables = {
  isMalformedManagedApiId,
  isMalformedOpId,
  buildConnectorIdentityIndex,
  repairAzureOpIdentity,
};

/**
 * Minimum age of a cached entry before {@link useBackgroundOperationsRefresh}
 * will trigger a network refetch on cache hit. Below this threshold the
 * cache is treated as "recent enough" — back-to-back reloads (Ctrl+R, Ctrl+R)
 * shouldn't cost another full pagination of /apiOperations.
 *
 * Kept short (5 min) because the cache can be poisoned by a silently
 * truncated `/apiOperations` pagination on the very first write for a
 * region: nothing errored, `hasNextPage` went false early, the write
 * gates passed, and the partial corpus established the HWM floor. The only
 * thing that can heal that case is a fresh end-to-end pagination that
 * returns more ops than the cache. With the per-tab `backgroundRefreshedScopes`
 * Set this still amortizes to one extra pagination per fresh tab per region.
 */
const BACKGROUND_REFRESH_MIN_CACHE_AGE_MS = 5 * 60 * 1000; // 5 minutes

/**
 * Per-scope record of background refreshes already attempted in this tab.
 * Keyed by `{project}::{region}`. Resets on full page reload, which is the
 * intended granularity — we only want to pay for the extra pagination once
 * per session per region, no matter how many times the picker is opened.
 *
 * Module-level so the entry survives the React lifecycle of any individual
 * `usePrefetchOperations` consumer (the picker can mount/unmount many times
 * while the user navigates).
 */
const backgroundRefreshedScopes = new Set<string>();

/** Test helper: clear the per-session refresh ledger. */
export const __resetBackgroundRefreshedScopesForTests = (): void => {
  backgroundRefreshedScopes.clear();
};

/**
 * Resolve the scope used by the operation-catalog queries.
 *   - `project`    — raw value from the store, sent to the backend as-is
 *                    (project names are treated as opaque identifiers).
 *   - `projectKey` — lowercased, for query/cache/scope keys so picker views
 *                    differing only in casing share a cache slot.
 *   - `region`     — lowercased; only used as a cache discriminator.
 */
const useScopeKey = () => {
  const selectedProject = useAppStore((s) => s.selectedProject);
  const selectedApp = useAppStore((s) => s.selectedApp);
  const selectedLocation = useAppStore((s) => s.selectedLocation);

  return useMemo(() => {
    const project = selectedProject ?? '';
    const projectKey = project.toLowerCase();
    const region = (selectedApp?.location ?? selectedLocation ?? '').toLowerCase();
    return { project, projectKey, region, hasScope: !!project && !!region };
  }, [selectedProject, selectedApp, selectedLocation]);
};

/**
 * Read-through to IndexedDB for the current scope. The result is loaded once
 * per scope-change and exposed so the lazy queries can skip network fetches
 * when a fresh cached corpus is available.
 */
const useCachedOperations = () => {
  const { region, hasScope } = useScopeKey();
  return useQuery({
    queryKey: ['operationsCache', region],
    queryFn: () => readOperationsCache(region),
    enabled: hasScope,
    ...queryOpts,
  });
};

/**
 * Connector identity index from `/managedApis`. Shares its query key with
 * `useAzureConnectorGroups` in `useOperations.ts` so React Query deduplicates
 * the network call across the picker's browse-mode hook and this one — no
 * extra round-trip.
 *
 * Returns the prebuilt Map so consumers don't re-derive it on every render.
 * Falls back to an empty Map while the fetch is in flight, which makes the
 * repair a no-op until the index hydrates (the merge useMemo below picks the
 * repair back up once the index resolves).
 */
const useAzureConnectorIdentityIndex = (): Map<string, { id: string; name: string }> => {
  const { getOttoToken } = useAuth();
  const { project, projectKey, hasScope } = useScopeKey();

  const { data: connectors } = useQuery({
    // Must match `useAzureConnectorGroups` exactly so React Query reuses the
    // cached result instead of issuing a second `/managedApis` pagination.
    queryKey: ['connectorGroups', 'azure', projectKey],
    enabled: hasScope && (!isLocalMode() || isArmMockMode()),
    queryFn: async () => {
      if (!project) return [];
      if (isLocalMode() && !isArmMockMode()) return [];
      const token = await getOttoToken();
      if (!token && !isArmMockMode()) return [];
      return fetchAllAzureConnectors({
        projectName: project,
        accessToken: token ?? '',
      });
    },
    ...queryOpts,
  });

  return useMemo(() => buildConnectorIdentityIndex(connectors), [connectors]);
};

/**
 * Lazy paginated query for Azure managed-API operations. The query stays in
 * loading state until the cache lookup resolves; when the cache holds a fresh
 * entry, we feed its Azure-typed operations as the first synthetic page so the
 * preloader sees `hasNextPage === false` and no network request fires.
 */
const useAzureOperationsLazyQuery = () => {
  const { getOttoToken } = useAuth();
  const { project, projectKey, region, hasScope } = useScopeKey();
  const { data: cached, isLoading: cacheLoading } = useCachedOperations();

  // Filter the cached corpus down to Azure-sourced operations (heuristic:
  // operations whose `type` indicates a managedApi operation). Treat an
  // empty result as a cache miss — a real Otto project always has thousands
  // of managed-API operations, so 0 means either the cache was written before
  // the network fetch completed (race) or the `type` discriminator drifted.
  // Either way, falling back to the network is correct.
  const cachedAzure = useMemo<DiscoveryOperation[] | null>(() => {
    if (!cached) return null;
    const azure = cached.operations.filter((op) => isAzureOperation(op));
    return azure.length > 0 ? azure : null;
  }, [cached]);

  return useInfiniteQuery({
    queryKey: ['operationsLazy', 'azure', projectKey, region],
    initialPageParam: undefined as string | undefined,
    enabled: hasScope && !cacheLoading,
    queryFn: async ({ pageParam }) => {
      if (cachedAzure) {
        // First (and only) synthetic page from cache. Returning no nextLink
        // ends pagination immediately.
        return { data: cachedAzure } satisfies PagedOperationsResult;
      }
      const token = await getOttoToken();
      // Block when we're cloud-bound without a token; local/arm-mock paths
      // tolerate an empty token via fetchWithAuth's mock branch.
      if (!token && !isLocalMode() && !isArmMockMode()) {
        return { data: [] } satisfies PagedOperationsResult;
      }
      return fetchAzureOperationsByPage(
        { projectName: project, accessToken: token ?? '' },
        pageParam,
      );
    },
    getNextPageParam: (lastPage) => lastPage.nextLink,
    ...queryOpts,
  });
};

/**
 * Custom-connector operations. Otto's control plane does not yet expose a
 * paginated custom-API operations endpoint, so this hook resolves to an empty
 * result and is wired purely for parity with the upstream three-source shape.
 * When the endpoint lands, swap this body for the real paginated fetch.
 */
const useCustomOperationsLazyQuery = () => {
  const { projectKey, region, hasScope } = useScopeKey();
  return useInfiniteQuery({
    queryKey: ['operationsLazy', 'custom', projectKey, region],
    initialPageParam: undefined as string | undefined,
    enabled: hasScope,
    queryFn: async (): Promise<PagedOperationsResult> => ({ data: [] }),
    getNextPageParam: () => undefined,
    ...queryOpts,
  });
};

/** Built-in (Logic App runtime) operations — single fetch. */
export const useBuiltInOperationsQuery = () => {
  const { getOttoToken } = useAuth();
  const { project, projectKey, hasScope } = useScopeKey();
  const selectedApp = useAppStore((s) => s.selectedApp);
  const { data: cached, isLoading: cacheLoading } = useCachedOperations();

  const enabled = hasScope && !!selectedApp?.name && !cacheLoading;

  return useQuery({
    queryKey: ['operations', 'builtin', projectKey, selectedApp?.name?.toLowerCase() ?? ''],
    enabled,
    queryFn: async (): Promise<DiscoveryOperation[]> => {
      if (cached) {
        return cached.operations.filter((op) => !isAzureOperation(op));
      }
      if (!selectedApp?.name) return [];
      const token = await getOttoToken();
      // Let the fetch error propagate so React Query marks status='error'.
      // The cache-write gate in usePrefetchOperations requires builtinStatus
      // === 'success' — swallowing here would let a failed fetch return [] as
      // a "success" and poison the IDB cache with a built-ins-less corpus.
      return fetchBuiltInOperations({
        projectName: project,
        appName: selectedApp.name,
        accessToken: token ?? '',
      });
    },
    ...queryOpts,
  });
};

/**
 * Merged corpus across all three sources plus the hardcoded client-side
 * built-ins (HTTP, Control, Variables, …). De-duplicates on operation id;
 * later sources override earlier ones so richer runtime metadata wins over
 * client fallbacks.
 *
 * `isLoading` is true while any source is still fetching its first page or has
 * more pages to fetch — callers should treat the data as "best-so-far" and
 * re-render as new pages arrive.
 */
export const useAllOperations = () => {
  const {
    data: azureOperations,
    isLoading: azureLoading,
    hasNextPage: azureHasNextPage,
  } = useAzureOperationsLazyQuery();
  const {
    data: customOperations,
    isLoading: customLoading,
    hasNextPage: customHasNextPage,
  } = useCustomOperationsLazyQuery();
  const { data: builtinOperations, isLoading: builtinLoading } = useBuiltInOperationsQuery();
  // Reliable identity source for normalizing malformed Azure-op `api.id`
  // / `api.name` fields. Empty Map until `/managedApis` resolves —
  // normalization is a no-op during that window, then re-runs via the
  // merge useMemo's dependency.
  const connectorIdentityIndex = useAzureConnectorIdentityIndex();

  const data = useMemo(() => {
    const merged = new Map<string, DiscoveryOperation>();
    const addAll = (
      ops: DiscoveryOperation[] | undefined,
      { repair = false }: { repair?: boolean } = {},
    ) => {
      if (!ops) return;
      for (const op of ops) {
        if (!op?.id) continue;
        // Repair runs even when the connector identity index is empty —
        // the {@link CANONICAL_DISPLAY_NAMES} fallback inside
        // `repairAzureOpIdentity` handles popular Microsoft / Google
        // brands that the tenant's `/managedApis` list may not include
        // (e.g. only publisher forks of Outlook.com exist, no canonical).
        // Dedupe on the REPAIRED id, not the original — malformed op.ids
        // of shape `/automationProjects/{p}/managedApis//{opName}` collide
        // across canonical brands and silently drop ops from the corpus
        // (see `isMalformedOpId`).
        if (repair) {
          const fixed = repairAzureOpIdentity(op, connectorIdentityIndex);
          merged.set(fixed.id, fixed);
        } else {
          merged.set(op.id, op);
        }
      }
    };

    // Client-side fallback baseline — provides Control/Variable/HTTP shells
    // immediately so the first paint shows something useful before any
    // network roundtrip resolves.
    addAll(getClientBuiltInOperations());

    // Normalize Azure ops only — the malformed-id bug is specific to the
    // `/apiOperations` endpoint. Custom + built-ins return well-formed ids.
    // Normalization rewrites stripped `api.id`
    // (e.g. `/automationProjects/{p}/managedApis/`) to canonical short
    // form (e.g. `/managedApis/sharepointonline`) by joining the op's
    // `api.displayName` against the `/managedApis` connector identity
    // index — the only field that survives the server's malformed-payload
    // shape. This single normalization pass replaces the previous
    // backfill architecture (which fired redundant per-connector fetches
    // to recover ops the bulk endpoint actually returned, just in
    // malformed shape).
    addAll(azureOperations?.pages.flatMap((page) => page.data), { repair: true });
    addAll(customOperations?.pages.flatMap((page) => page.data));
    // Runtime built-ins ship their own Compose entry under the "Data
    // Operations New" connector label, which collides with Otto's curated
    // client-side Compose (id 'compose'). The two entries have different
    // ids so the Map dedupe above can't collapse them — strip the runtime
    // copy here so the search corpus only exposes the curated manifest.
    // Mirrors the same guard in useSearchOperations (useOperations.ts).
    addAll(
      builtinOperations?.filter(
        (op) => op.properties?.operationType?.toLowerCase() !== 'compose',
      ),
    );

    return Array.from(merged.values());
  }, [
    azureOperations,
    customOperations,
    builtinOperations,
    connectorIdentityIndex,
  ]);

  const isLoading = useMemo(
    () =>
      azureLoading ||
      customLoading ||
      builtinLoading ||
      azureHasNextPage ||
      customHasNextPage ||
      false,
    [
      azureLoading,
      customLoading,
      builtinLoading,
      azureHasNextPage,
      customHasNextPage,
    ],
  );

  return useMemo(() => ({ data, isLoading }), [data, isLoading]);
};

/** Triggers-only view of {@link useAllOperations}. */
export const useAllTriggers = () => {
  const all = useAllOperations();
  return useMemo(
    () => ({
      ...all,
      data: all.data.filter((op) => !!op.properties?.trigger),
    }),
    [all],
  );
};

/** Actions-only view of {@link useAllOperations}. */
export const useAllActions = () => {
  const all = useAllOperations();
  return useMemo(
    () => ({
      ...all,
      data: all.data.filter((op) => !op.properties?.trigger),
    }),
    [all],
  );
};

/**
 * Self-healing background refresh for the IDB cache.
 *
 * When the picker hydrates from a cache entry that's older than
 * {@link BACKGROUND_REFRESH_MIN_CACHE_AGE_MS}, this hook silently re-fetches
 * the Azure operations corpus end-to-end (all pages) and compares the result
 * against the cached payload. If the network corpus is materially larger, it
 * overwrites the cache entry and invalidates the in-memory queries so the
 * picker picks up the recovered data without a page reload.
 *
 * The purpose is recovery: a previously-stuck partial cache (e.g. a one-off
 * transient that wrote a truncated corpus, or a v3-initial entry without a
 * high-water-mark) gets healed automatically the next time the picker mounts.
 * Without this, a partial cache persists for the full 24h TTL and the user
 * can't search for connectors that aren't in the cached subset.
 *
 * Runs at most once per `{project, region}` per browser tab session.
 */
const useBackgroundOperationsRefresh = () => {
  const queryClient = useQueryClient();
  const { getOttoToken } = useAuth();
  const { project, projectKey, region, hasScope } = useScopeKey();
  const { data: cached } = useCachedOperations();

  useEffect(() => {
    if (!hasScope || !cached) return;
    if (Date.now() - cached.cachedAt < BACKGROUND_REFRESH_MIN_CACHE_AGE_MS) {
      return; // Cache is recent enough — skip the refresh to spare API load.
    }
    const scopeKey = `${projectKey}::${region}`;
    if (backgroundRefreshedScopes.has(scopeKey)) return;
    backgroundRefreshedScopes.add(scopeKey);

    let cancelled = false;
    const run = async (): Promise<void> => {
      try {
        const token = await getOttoToken();
        if (!token && !isLocalMode() && !isArmMockMode()) return;

        // Page through /apiOperations end-to-end. Any error rejects the loop
        // immediately so a partial network response can't sneak in. In
        // parallel, refetch `/managedApis` so we can repair the malformed
        // `api.id` on each operation before persisting — without this the
        // recovered cache would still surface canonical Outlook / Office 365
        // ops under a `displayName:` group key and downstream consumers
        // (drill-in fetch, slug extraction) would silently break.
        const fresh: DiscoveryOperation[] = [];
        const connectorListPromise = fetchAllAzureConnectors({
          projectName: project,
          accessToken: token ?? '',
        }).catch((err) => {
          // Non-fatal — fall back to no repair (Map stays empty, ops persisted as-is).
          console.warn(
            `[useBackgroundOperationsRefresh] /managedApis fetch failed for '${region}' ` +
              `(repair skipped):`,
            err,
          );
          return [] as Connector[];
        });
        let pageParam: string | undefined;
        do {
          const page = await fetchAzureOperationsByPage(
            { projectName: project, accessToken: token ?? '' },
            pageParam,
          );
          if (cancelled) return;
          fresh.push(...page.data);
          pageParam = page.nextLink;
        } while (pageParam);

        if (cancelled) return;

        const connectorList = await connectorListPromise;
        if (cancelled) return;
        const repairIndex = buildConnectorIdentityIndex(connectorList);
        const freshRepaired = fresh.map((op) =>
          repairAzureOpIdentity(op, repairIndex),
        );

        const cachedAzureCount = cached.operations.filter(isAzureOperation).length;
        // Only overwrite when the fresh corpus is strictly larger. The HWM
        // gate inside writeOperationsCache will *also* enforce the 80%
        // floor as a second line of defense.
        if (freshRepaired.length <= cachedAzureCount) {
          return;
        }

        // Merge fresh Azure ops with the non-Azure portion of the cached
        // corpus (built-ins, custom). The non-Azure half is unaffected by
        // this refresh; preserving it avoids dropping built-ins until the
        // built-in source independently refetches.
        const nonAzure = cached.operations.filter((op) => !isAzureOperation(op));
        const merged = [...nonAzure, ...freshRepaired].map(trimOperationForCache);

        const accepted = await writeOperationsCache(region, merged);
        if (cancelled || !accepted) return;

        // Invalidate the cache read query *and* both source queries so the
        // picker re-renders against the recovered corpus. The Azure lazy
        // query is keyed by {projectKey, region}; the built-in query is keyed
        // by {projectKey, appName} — use a predicate match for that one.
        queryClient.invalidateQueries({ queryKey: ['operationsCache', region] });
        queryClient.invalidateQueries({
          queryKey: ['operationsLazy', 'azure', projectKey, region],
        });
        queryClient.invalidateQueries({
          predicate: (q) => {
            const k = q.queryKey;
            return (
              Array.isArray(k) &&
              k[0] === 'operations' &&
              k[1] === 'builtin' &&
              k[2] === projectKey
            );
          },
        });
      } catch (err) {
        console.warn(
          `[useBackgroundOperationsRefresh] Refresh failed for '${region}':`,
          err,
        );
      }
    };

    void run();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasScope, cached, project, projectKey, region]);
};

/**
 * Root-component preloader. Drives all three lazy queries forward, fetching
 * the next Azure/Custom page as soon as the previous one settles, and
 * persists the merged corpus to IndexedDB once every source reports done.
 *
 * Returns `{ isLoading }` — true while any source still has pages outstanding
 * or its first fetch is in flight. Callers should NOT block UI on this — the
 * picker is expected to render immediately and degrade search quality
 * gracefully as new pages arrive.
 */
export const usePrefetchOperations = () => {
  const queryClient = useQueryClient();
  const { projectKey, region, hasScope } = useScopeKey();

  // Self-healing path: if the cache is older than the freshness window,
  // re-fetch in the background and overwrite if we recover more ops than
  // the cache currently holds. Recovers users stuck with a partial entry.
  useBackgroundOperationsRefresh();

  // Connector identity index — used by the persistence diagnostic below
  // so canonical-strict counts agree with the merged-corpus log (both
  // resolve ops with stripped `api.id` + missing `api.name` via the
  // canonical `api.displayName`).
  const connectorIdentityIndex = useAzureConnectorIdentityIndex();

  const {
    isLoading: azureIsLoading,
    fetchNextPage: fetchNextAzurePage,
    hasNextPage: hasNextAzurePage,
    isFetchingNextPage: isFetchingNextAzurePage,
    status: azureStatus,
  } = useAzureOperationsLazyQuery();

  useEffect(() => {
    if (azureIsLoading || isFetchingNextAzurePage) return;
    if (!hasNextAzurePage) return;
    void fetchNextAzurePage();
  }, [azureIsLoading, fetchNextAzurePage, hasNextAzurePage, isFetchingNextAzurePage]);

  const {
    isLoading: customIsLoading,
    fetchNextPage: fetchNextCustomPage,
    hasNextPage: hasNextCustomPage,
    isFetchingNextPage: isFetchingNextCustomPage,
    status: customStatus,
  } = useCustomOperationsLazyQuery();

  useEffect(() => {
    if (customIsLoading || isFetchingNextCustomPage) return;
    if (!hasNextCustomPage) return;
    void fetchNextCustomPage();
  }, [customIsLoading, fetchNextCustomPage, hasNextCustomPage, isFetchingNextCustomPage]);

  const { isLoading: builtinIsLoading, status: builtinStatus } = useBuiltInOperationsQuery();

  const isLoading = useMemo(
    () =>
      azureIsLoading ||
      customIsLoading ||
      builtinIsLoading ||
      hasNextAzurePage ||
      hasNextCustomPage,
    [
      azureIsLoading,
      customIsLoading,
      builtinIsLoading,
      hasNextAzurePage,
      hasNextCustomPage,
    ],
  );

  // Persist the merged corpus once every source has finished. Track the most
  // recent {scope, count, malformed} we've written so reactive re-renders
  // don't replay the IDB write repeatedly. The malformed-id count is part of
  // the fingerprint so a repair-induced quality improvement (same op count,
  // fewer malformed `api.id`s) re-triggers a write — without this, a cache
  // poisoned during the first fetch (where `/apiOperations` finished before
  // `/managedApis` resolved the identity index) would stay poisoned for the
  // full 24h TTL even after the in-memory repair fixed the corpus.
  const { data: cached } = useCachedOperations();
  const lastWritten = useRef<{ key: string; count: number; malformed: number } | null>(null);
  const { data: merged } = useAllOperations();

  useEffect(() => {
    if (!hasScope || isLoading) return;
    if (!merged || merged.length === 0) return;

    // Cache hit — only write back when the in-memory corpus is strictly
    // higher quality than the persisted one (i.e. the `/managedApis`
    // identity index resolved and the repair fixed malformed `api.id`s the
    // cached snapshot was missing). Avoids resetting `cachedAt` on every
    // mount while still letting a poisoned cache heal itself within one
    // session instead of waiting for the full TTL to expire.
    const inMemoryMalformed = countMalformedAzureIds(merged);
    if (cached) {
      const cachedMalformed = countMalformedAzureIds(cached.operations);
      if (inMemoryMalformed >= cachedMalformed) return;
    }

    // Refuse to persist a partial corpus. If any source ended in an error
    // state (e.g. a transient 429 / 503 / network blip during page N of M,
    // or a built-in /operations fetch that errored), the in-memory corpus
    // contains only the pages/sources that succeeded — writing that subset
    // to IndexedDB would lock users into an incomplete operation list for
    // the entire 24h TTL. Wait for the next clean run; TanStack Query
    // auto-retries failed queries and a subsequent settle will satisfy
    // this gate.
    if (
      azureStatus !== 'success' ||
      customStatus !== 'success' ||
      builtinStatus !== 'success'
    ) {
      return;
    }

    // Guard against poisoning the cache with a built-ins-only corpus. If the
    // Azure source hasn't returned anything yet (or its `type` discriminator
    // doesn't match `isAzureOperation`), persisting now would short-circuit
    // every future load via the cached-fast-path in `useAzureOperationsLazyQuery`.
    // Bail out and wait for a subsequent settle that includes Azure ops.
    const azureCount = merged.filter(isAzureOperation).length;
    if (azureCount === 0) {
      return;
    }

    const scopeKey = `${projectKey}::${region}`;
    if (
      lastWritten.current &&
      lastWritten.current.key === scopeKey &&
      lastWritten.current.count === merged.length &&
      lastWritten.current.malformed === inMemoryMalformed
    ) {
      return;
    }
    lastWritten.current = {
      key: scopeKey,
      count: merged.length,
      malformed: inMemoryMalformed,
    };
    // Trim before persisting — the in-memory TanStack cache keeps the full
    // payload for any consumer that needs richer metadata in the live
    // session; IDB stores only the slim shape the picker + search index read.
    const trimmed = merged.map(trimOperationForCache);

    void writeOperationsCache(region, trimmed).then((accepted) => {
      if (accepted) {
        // Refresh the cache query so subsequent mounts see the fresh entry
        // and can short-circuit the network path.
        queryClient.invalidateQueries({ queryKey: ['operationsCache', region] });
      }
      // If `accepted === false`, the cache module already logged a warning
      // with the HWM details — nothing else to do here.
    });
  }, [hasScope, isLoading, merged, projectKey, region, cached, queryClient, azureStatus, customStatus, builtinStatus, connectorIdentityIndex]);

  return { isLoading };
};

/**
 * Heuristic for splitting the cached corpus back into "Azure" vs "BuiltIn"
 * buckets when hydrating from IndexedDB. Mirrors the type discriminator the
 * Otto control plane returns for managed-api operations.
 */
const isAzureOperation = (op: DiscoveryOperation): boolean =>
  (op.type ?? '').toLowerCase() === 'automationprojects/managedapis/apioperations';
